import { useMemo } from "react";
import { useCategory } from "../../../queries/useCategories";
import { useCategoryAttributesWithValues } from "../../../queries/useCategoryAttributesWithValues";
import type { Category } from "@/services/api/categories";
import type { CategoryAttributeResponse } from "@/services/api";
import type { CategoryAttributeRequest } from "@/services/api/categories";

export interface DisplayAttribute {
  id: number;
  name: string;
  displayName: string;
  description?: string;
  dataType: string;
  isRequired: boolean;
  isSearchable: boolean;
  categoryId?: string | number;
  order: number;
  values: Array<{ id: number; value: string; order: number }>;
}

interface UseCategoryAttributesDisplayOptions {
  category: Category | null;
  enabled: boolean;
}

function transformToDisplayAttributes(
  categoryAttributes: CategoryAttributeRequest[],
  availableAttributes: CategoryAttributeResponse[],
  categoryId?: string | number
): DisplayAttribute[] {
  if (categoryAttributes.length === 0 || availableAttributes.length === 0) {
    return [];
  }

  const attributesMap = new Map(
    availableAttributes.map((attr) => [attr.id, attr])
  );

  return categoryAttributes
    .map((catAttr): DisplayAttribute | null => {
      const fullAttr = attributesMap.get(catAttr.attributeId);
      if (!fullAttr) return null;

      return {
        id: fullAttr.id,
        name: fullAttr.name,
        displayName: fullAttr.name,
        dataType: "enum",
        isRequired: catAttr.isMandatory ?? false,
        isSearchable: false,
        categoryId,
        order: catAttr.order ?? 0,
        values: fullAttr.values,
      };
    })
    .filter((attr): attr is DisplayAttribute => attr !== null);
}

export function useCategoryAttributesDisplay({
  category,
  enabled,
}: UseCategoryAttributesDisplayOptions) {
  const { data: availableAttributes = [], isLoading: isLoadingAttributes } =
    useCategoryAttributesWithValues({ enabled });
  const { data: categoryDetails, isLoading: isLoadingCategory } = useCategory(
    category?.id || "",
    { enabled: enabled && !!category?.id }
  );

  const attributes = useMemo(() => {
    if (
      !categoryDetails?.attributes ||
      availableAttributes.length === 0 ||
      !category?.id
    ) {
      return [];
    }

    return transformToDisplayAttributes(
      categoryDetails.attributes,
      availableAttributes,
      category.id
    );
  }, [categoryDetails?.attributes, availableAttributes, category?.id]);

  return {
    attributes,
    isLoading: isLoadingCategory || isLoadingAttributes,
  };
}
